import { useQuery, useQueryClient } from '@tanstack/react-query'
import req from '@sportninja/common/api/request'
import { queryKeys } from '@sportninja/common/config/queryKeys'

export interface Highlight {
  id: string
  game_id: string
  player_id: string
  team_id: string
  title: string
  body: string
  video_url: string
  thumbnail_url: string
  video_duration: string
  type?: string
  teamSide?: 'Home' | 'Visitor'
  playerName?: string
  teamName?: string
}

const HIGHLIGHTS_STALE_TIME = 2 * 60 * 1000 // 2 minutes

const fetchHighlights = async (gameId: string): Promise<Highlight[]> => {
  const response = await req(`/games/${gameId}/highlights`)
  return response.data
}

/**
 * Custom hook for fetching game highlights, backed by React Query.
 * Cached per gameId for 2 minutes — re-fetches only happen after the
 * stale window or when refetch() is called explicitly.
 */
export const useHighlights = (gameId?: string) => {
  const query = useQuery<Highlight[]>({
    queryKey: queryKeys.gameHighlights.byGame(gameId ?? ''),
    queryFn: () => fetchHighlights(gameId as string),
    enabled: Boolean(gameId),
    staleTime: HIGHLIGHTS_STALE_TIME,
  })

  return {
    data: query.data ?? [],
    loading: query.isPending,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Imperative variant — for callers that need to fetch (and warm the
 * cache) outside of a render, e.g., during pull-to-refresh.
 * Shares the same cache as useHighlights.
 */
export const fetchHighlightsWithCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  gameId: string
) =>
  queryClient.fetchQuery({
    queryKey: queryKeys.gameHighlights.byGame(gameId),
    queryFn: () => fetchHighlights(gameId),
    staleTime: HIGHLIGHTS_STALE_TIME,
  })
